import {
  pblLanguages,
  pblLawFirmPositions,
  pblConsultationMethods,
  pblLegalServiceNeeds,
  pblProfessionalQualifications,
  pblServiceAreas,
} from '@/data/pbl';

export type PblDisplayTranslator = (key: string, options?: { defaultValue?: string }) => string;

type TranslationEntry = {
  labelKey: string;
  defaultLabel: string;
};

type PblDashboardLabelSource = {
  key?: string | null;
  label?: string | null;
};

const normalizePblDisplayValue = (value?: string | null) =>
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const pblDisplayValueEntries = new Map<string, TranslationEntry>();

const registerDisplayValue = (entry: TranslationEntry, aliases: Array<string | undefined | null>) => {
  aliases.forEach((alias) => {
    const normalizedAlias = normalizePblDisplayValue(alias);
    if (normalizedAlias) pblDisplayValueEntries.set(normalizedAlias, entry);
  });
};

const registerOptionValues = <Option extends { value: string; defaultLabel: string; labelKey: string }>(
  options: readonly Option[]
) => {
  options.forEach((option) => {
    registerDisplayValue(
      {
        labelKey: option.labelKey,
        defaultLabel: option.defaultLabel,
      },
      [option.value, option.defaultLabel]
    );
  });
};

registerOptionValues(pblServiceAreas);
registerOptionValues(pblLegalServiceNeeds);
registerOptionValues(pblConsultationMethods);
registerOptionValues(pblLanguages);
registerOptionValues(pblLawFirmPositions);
registerOptionValues(pblProfessionalQualifications);

registerDisplayValue(
  {
    labelKey: 'pbl.serviceArea.estatePlanning',
    defaultLabel: 'Estate Planning',
  },
  ['Estate Planning', 'Estate planning', 'Estate planning issues']
);
registerDisplayValue(
  {
    labelKey: 'pbl.serviceArea.familyMatters',
    defaultLabel: 'Family Matters',
  },
  ['Family matters']
);
registerDisplayValue(
  {
    labelKey: 'pbl.serviceArea.buildingManagement',
    defaultLabel: 'Building Management',
  },
  ['Building management']
);
registerDisplayValue(
  {
    labelKey: 'pbl.serviceArea.tenancyAndProperties',
    defaultLabel: 'Tenancy and properties related Matters',
  },
  ['Tenancy and properties related matters']
);
registerDisplayValue(
  {
    labelKey: 'pbl.serviceArea.criminalMatters',
    defaultLabel: 'Criminal Matters',
  },
  ['Criminal matters']
);

const pblDashboardEntries = new Map<string, TranslationEntry>();

const registerDashboardLabel = (
  key: string,
  labelKey: string,
  defaultLabel: string,
  aliases: string[] = []
) => {
  const entry = { labelKey, defaultLabel };
  pblDashboardEntries.set(normalizePblDisplayValue(key), entry);
  registerDisplayValue(entry, [defaultLabel, ...aliases]);
};

registerDashboardLabel(
  'office_cases',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.officeCases',
  'Office cases'
);
registerDashboardLabel(
  'pending_review',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.pendingReview',
  'Pending review'
);
registerDashboardLabel(
  'matching_attention',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.matchingAttention',
  'Matching attention'
);
registerDashboardLabel(
  'pending_appointment_confirmation',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.meetingConfirmation',
  'Meeting confirmation',
  ['Pending appointment confirmation']
);
registerDashboardLabel(
  'assigned_cases',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.assignedCases',
  'Assigned cases'
);
registerDashboardLabel('my_cases', 'pbl.pagesHardcoded.roleDashboard.summaryCards.myCases', 'My cases');
registerDashboardLabel(
  'pending_oc_review',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.pendingOcReview',
  'Pending OC review'
);
registerDashboardLabel(
  'pending_timeslot_selection',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.timeslotSelection',
  'Timeslot selection'
);
registerDashboardLabel(
  'pending_selection',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.pendingTimeSelection',
  'Pending time selection'
);
registerDashboardLabel(
  'pending_rating',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.pendingFeedback',
  'Pending feedback'
);
registerDashboardLabel(
  'pending_invitations',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.pendingInvitations',
  'Pending invitations'
);
registerDashboardLabel(
  'pending_timeslot',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.timeslotsToPropose',
  'Timeslots to propose'
);
registerDashboardLabel(
  'pending_signature',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.pendingSignatures',
  'Pending signatures'
);
registerDashboardLabel(
  'lawyer_vetting',
  'pbl.pagesHardcoded.roleDashboard.summaryCards.lawyerVetting',
  'Lawyer vetting'
);
registerDashboardLabel(
  'review_cases',
  'pbl.pagesHardcoded.roleDashboard.dashboardLabels.reviewSubmittedCases',
  'Review submitted cases'
);
registerDashboardLabel(
  'choose_time',
  'pbl.pagesHardcoded.roleDashboard.dashboardLabels.chooseMeetingTime',
  'Choose meeting time'
);

const translateEntry = (t: PblDisplayTranslator, entry: TranslationEntry) =>
  t(entry.labelKey, { defaultValue: entry.defaultLabel });

export const translatePblDisplayValue = (
  t: PblDisplayTranslator,
  value?: string | null
) => {
  const text = value?.trim() || '';
  if (!text) return value || '';

  const entry = pblDisplayValueEntries.get(normalizePblDisplayValue(text));
  return entry ? translateEntry(t, entry) : text;
};

export const translatePblDisplayList = (
  t: PblDisplayTranslator,
  value?: string | string[] | null
) => {
  const values = Array.isArray(value)
    ? value
    : (value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

  return values.map((item) => translatePblDisplayValue(t, item)).join(', ');
};

export const translatePblDashboardLabel = (
  t: PblDisplayTranslator,
  source: PblDashboardLabelSource
) => {
  const normalizedKey = normalizePblDisplayValue(source.key);
  const normalizedLabel = normalizePblDisplayValue(source.label);

  if (normalizedKey === 'total cases') {
    return translateEntry(
      t,
      normalizedLabel === 'total cases'
        ? {
            labelKey: 'pbl.pagesHardcoded.roleDashboard.summaryCards.totalCases',
            defaultLabel: 'Total cases',
          }
        : {
            labelKey: 'pbl.pagesHardcoded.roleDashboard.summaryCards.myCases',
            defaultLabel: 'My cases',
          }
    );
  }

  if (normalizedKey === 'upcoming meetings') {
    return translateEntry(
      t,
      normalizedLabel === 'scheduled meetings'
        ? {
            labelKey: 'pbl.pagesHardcoded.roleDashboard.summaryCards.scheduledMeetings',
            defaultLabel: 'Scheduled meetings',
          }
        : {
            labelKey: 'pbl.pagesHardcoded.roleDashboard.summaryCards.upcomingMeetings',
            defaultLabel: 'Upcoming meetings',
          }
    );
  }

  const keyEntry = pblDashboardEntries.get(normalizedKey);
  if (keyEntry) return translateEntry(t, keyEntry);

  return translatePblDisplayValue(t, source.label);
};
